import { ReactNode, useRef } from "react";
import clsx from "clsx";
import { twMerge } from "tailwind-merge";

// Interfaces
import { ISmartInputProps } from "../interfaces";

// Helpers
import { getPlaceholder } from "../utils";

// Layouts
import TooltipText from "@/Layouts/components/Tooltip/TooltipText";

// MUI
import { TextField, Tooltip } from "@mui/material";

// Utils
const getSanitizedParams = (args: ISmartInputProps) => {
	const { warning = false, error = false, disabled = false, highlighted = false } = args || {};
	return {
		warning,
		error,
		disabled,
		highlighted: !error && highlighted,
		regular: !error && !highlighted,
	};
};

const getBasicClasses = () => {
	return clsx({
		"!rounded-[8px] [&_.MuiInputBase-root]:!pl-[10px] [&_.MuiInputBase-root]:!py-[4.5px] [&_.MuiInputBase-root]:!min-h-[30px]": true,
		"[&_.MuiInputBase-root_.MuiInputBase-input]:!font-sans [&_.MuiInputBase-root>fieldset]:!border-[1px]": true,
		"[&_.MuiInputBase-root_.MuiAutocomplete-input]:!p-[0px] [&_.MuiInputBase-root_.MuiAutocomplete-endAdornment_.MuiAutocomplete-popupIndicator]:!p-[4px]":
			true,
		"[&_.MuiInputBase-root>fieldset]:rounded-[6px] [&_.MuiInputBase-root>fieldset]:!top-[0px]": true,
		"[&_.MuiInputBase-root_.MuiInputBase-input]:!text-[14.5px]": true,
		"[&_.MuiAutocomplete-tag]:!font-sans [&_.MuiAutocomplete-tag]:!text-[14.5px]": true,
	});
};

const getFieldsClasses = (args: ISmartInputProps) => {
	const { warning = false, error = false, disabled = false, highlighted = false, regular = false } = getSanitizedParams(args) || {};
	return clsx({
		"!bg-light-warning": warning,
		"!bg-white": !warning || !error,
		"!bg-light-error": error,
		"[&_.MuiInputBase-root>fieldset]:!border-disabled [&_.MuiInputBase-root>fieldset]:hover:!border-disabled": regular,
		"[&_.MuiInputBase-root>fieldset]:!border-warning [&_.MuiInputBase-root>fieldset]:!border-1": warning,
		"[&_.MuiInputBase-root>fieldset]:hover:!border-warning [&_.MuiInputBase-root>fieldset]:hover:!border-1": warning,
		"[&_.MuiInputBase-root>fieldset]:!border-error [&_.MuiInputBase-root>fieldset]:!border-1": error,
		"[&_.MuiInputBase-root>fieldset]:hover:!border-error [&_.MuiInputBase-root>fieldset]:hover:!border-1": error,
		"[&_.MuiInputBase-root>fieldset]:!border-primary [&_.MuiInputBase-root>fieldset]:hover:!border-primary": highlighted,
		"[&_.MuiInputBase-root_.MuiInputBase-input]:!text-default [&_.MuiAutocomplete-tag]:!text-default": !disabled,
		"[&_.MuiInputBase-root_.MuiInputBase-input]:!text-disabled [&_.MuiAutocomplete-tag]:!text-disabled": disabled,
	});
};

const getDisplayClass = (hidden = false) => {
	if (!hidden) {
		return "";
	}
	return "[&_.MuiInputBase-root_.MuiInputBase-input]:!min-w-0";
};

const isEllipsisActive = (e: HTMLElement | null) => e && e.offsetWidth < e.scrollWidth;

const getTooltip = (labels = "", hidden = false) => {
	if (!hidden || !labels) {
		return "";
	}
	return <TooltipText text={labels} />;
};

const getInputProps = (args: { params?: unknown; startAdornment: ReactNode }) => {
	const { params, startAdornment } = args;
	const inputProps = params?.["InputProps" as keyof object] ?? {};

	if (!startAdornment) {
		return inputProps;
	}

	return {
		...inputProps,
		startAdornment,
	};
};

export default function RenderInputField(props: Readonly<ISmartInputProps>) {
	const {
		params = null,
		selectorProps = null,
		sx = null,
		value = null,
		startAdornment = null,
		classes = "",
		placeholder = "",
		disabled = false,
		hideTooltip,
		warning = false,
		error = false,
		hidden = false,
		highlighted = false,
		multiple = false,
		open = false,
		onInputChange = () => {},
	} = props;

	const { showBy = "label" } = selectorProps || {};

	const basicClasses = getBasicClasses();
	const fieldClasses = getFieldsClasses({ warning, error, disabled, highlighted });
	const inputClass = getDisplayClass(hidden);

	// Required for tooltip behavior
	const inputRef = useRef(null);
	const title = value ? value[showBy as keyof object] : "";
	const isTooltipActive = isEllipsisActive(inputRef.current);
	const tooltip = getTooltip(title, Boolean(!multiple && isTooltipActive && !open));

	// Placeholder behavior
	const placeholderValue = getPlaceholder(placeholder, { value, multiple, open });

	// Default Input Props
	const inputProps = getInputProps({ params, startAdornment });

	return (
		<Tooltip arrow title={tooltip} placement={"top"} open={hideTooltip ? !hideTooltip : undefined}>
			<TextField
				{...params}
				fullWidth
				hiddenLabel
				inputRef={inputRef}
				error={error}
				placeholder={placeholderValue}
				className={twMerge(basicClasses, fieldClasses, classes, inputClass)}
				InputProps={{
					...inputProps,
				}}
				sx={{
					...sx,
					"& .MuiInputBase-input": {
						"&::placeholder": {
							opacity: "1 !important",
							color: "var(--disabled-and-stroke-color) !important",
						},
						WebkitTextFillColor: "unset !important",
					},
				}}
				onChange={(evt) => {
					onInputChange?.(evt?.target?.value);
				}}
			/>
		</Tooltip>
	);
}
